import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useAdminInfluencers,
  useUpsertInfluencer,
  useDeleteInfluencer,
  EMPTY_INFLUENCER,
  type InfluencerRow,
} from '@/lib/admin-queries';
import { InfluencerEditForm } from '@/features/influencers/InfluencerEditForm';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/Toast';

export function InfluencersPage() {
  const { data: rows = [], isLoading } = useAdminInfluencers();
  const upsert = useUpsertInfluencer();
  const remove = useDeleteInfluencer();
  const [editing, setEditing] = useState<InfluencerRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<InfluencerRow | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (r: InfluencerRow) => (
          <div>
            <p className="font-medium">{r.name}</p>
            {r.headline && <p className="text-xs text-fg-subtle">{r.headline}</p>}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: InfluencerRow) => (
          <Badge variant={r.status === 'published' ? 'gold' : 'default'}>{r.status}</Badge>
        ),
      },
      {
        key: 'joined',
        header: 'Joined',
        cell: (r: InfluencerRow) => (
          <span className="text-xs text-fg-muted">{r.joined_at ? formatDate(r.joined_at) : '—'}</span>
        ),
      },
      {
        key: 'social',
        header: 'Social',
        cell: (r: InfluencerRow) => {
          const count = [r.tiktok_url, r.instagram_url, r.youtube_url, r.facebook_url].filter(Boolean).length;
          return <span className="text-xs text-fg-muted">{count} link{count === 1 ? '' : 's'}</span>;
        },
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: InfluencerRow) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: InfluencerRow) {
    try {
      await upsert.mutateAsync(row);
      toast.success(row.id ? 'Influencer updated' : 'Influencer created');
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Influencer deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Content Creation</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Influencers</h1>
          <p className="mt-2 text-sm text-fg-muted">Talent attached to your short-drama series. Each influencer can be linked to many dramas.</p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditing({ ...EMPTY_INFLUENCER });
            setCreating(true);
          }}
        >
          New influencer
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No influencers yet. Click New influencer to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={creating ? 'New influencer' : 'Edit influencer'}
        description="Changes save on submit."
        size="xl"
      >
        {editing && (
          <InfluencerEditForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this influencer?"
        description={
          deleting
            ? `"${deleting.name}" will be removed from the marketing site. Any dramas linked to this influencer will lose the link. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete influencer"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}
